import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, FileText, IndianRupee, Search, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { openGstInvoice, type GstInvoice } from '../invoiceTemplate';

/** First & last calendar day of a YYYY-MM month, as YYYY-MM-DD. */
function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const last = new Date(Date.UTC(y, m, 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last) };
}

/** Rolling window ending today, going back N calendar months. */
function lastNMonthsRange(n: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - n);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

const QUICK_RANGES = [
  { label: '1M', months: 1 },
  { label: '2M', months: 2 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '12M', months: 12 },
];

// Plain Month/Year dropdowns instead of a native <input type="month">: the
// native picker's placeholder/format follows the browser's OS locale, and on
// some Chromium builds that spells out the full month name ("September, ----")
// regardless of the `lang` attribute — these selects render identically
// everywhere.
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const monthNumOptions = MONTH_NAMES.map((name, i) => ({
  value: String(i + 1).padStart(2, '0'),
  label: name,
}));
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

interface GstTxn {
  booking_id: string;
  created_at: string;
  customer_gstin: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  delivery_charge: number | null;
  gst_percent: number | null;
  cgst_amount: number | null;
  sgst_amount: number | null;
  gst_amount: number | null;
  total: number | null;
  payment_method: string | null;
  payment_status: string | null;
}
interface GstReport {
  summary: {
    total_gst: number; total_cgst: number; total_sgst: number;
    total_taxable: number; invoice_count: number;
    b2b_count: number; b2c_count: number; b2b_gst: number; b2c_gst: number;
  };
  transactions: GstTxn[];
  total: number; page: number; limit: number; total_pages: number;
}

type GstType = 'all' | 'b2b' | 'b2c';

const inr = (n: number | null | undefined) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

/** "YYYY-MM-DD" -> local Date (avoids UTC-parsing day-shift), and back. */
const parseISODate = (s: string): Date | undefined => (s ? new Date(`${s}T00:00:00`) : undefined);
const toISODate = (d: Date | undefined): string =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

export function GstReportPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [gstin, setGstin] = useState('');
  const [type, setType] = useState<GstType>('all');
  const [page, setPage] = useState(1);
  const [invoicingId, setInvoicingId] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  // applied filters (only change on Apply)
  const [filters, setFilters] = useState<{ from?: string; to?: string; gstin?: string; type?: string }>({});

  const { data, isLoading } = useQuery({
    queryKey: ['gst-report', filters, page],
    queryFn: async (): Promise<GstReport> => {
      const res = await api.get(API_ENDPOINTS.REPORTS.GST, {
        params: { ...filters, page, limit: 50 },
      });
      return (res.data as any)?.data ?? res.data;
    },
  });

  // The Month field is two plain selects (month name + year), each with its
  // own state — deriving both from a single combined "YYYY-MM" string meant a
  // lone selection (month picked, year not yet) got wiped on every render.
  // Once both are picked, auto-fill the from/to date range.
  const [monthNum, setMonthNum] = useState('');
  const [yearNum, setYearNum] = useState('');
  const onMonthPartChange = (part: 'month' | 'year', value: string) => {
    const m = part === 'month' ? value : monthNum;
    const y = part === 'year' ? value : yearNum;
    setMonthNum(m);
    setYearNum(y);
    if (m && y) {
      const r = monthRange(`${y}-${m}`);
      setFrom(r.from);
      setTo(r.to);
    }
  };
  const clearMonthSelectors = () => { setMonthNum(''); setYearNum(''); };

  const apply = () => {
    setPage(1);
    setFilters({
      from: from || undefined,
      to: to || undefined,
      gstin: gstin || undefined,
      type: type !== 'all' ? type : undefined,
    });
  };

  // Quick "last N months" range — sets from/to and applies immediately,
  // rather than requiring a separate Apply click.
  const applyQuickRange = (months: number) => {
    const r = lastNMonthsRange(months);
    clearMonthSelectors();
    setFrom(r.from);
    setTo(r.to);
    setPage(1);
    setFilters({
      from: r.from,
      to: r.to,
      gstin: gstin || undefined,
      type: type !== 'all' ? type : undefined,
    });
  };

  const clearFilters = () => {
    clearMonthSelectors(); setFrom(''); setTo(''); setGstin(''); setType('all');
    setPage(1);
    setFilters({});
  };

  const handleDownloadAllPdf = async () => {
    setDownloadingAll(true);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.GST_INVOICES_PDF, {
        params: filters,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gst-invoices-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded combined GST invoices PDF');
    } catch (e: any) {
      const status = e?.response?.status;
      toast.error(
        status === 404
          ? 'No invoices match the selected filters.'
          : 'Could not generate the combined PDF. Try narrowing the date range.',
      );
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleExportGstReport = async () => {
    setExportingReport(true);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.GST_REPORT_PDF, {
        params: filters,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gst-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('GST report downloaded');
    } catch (e: any) {
      const status = e?.response?.status;
      toast.error(
        status === 404
          ? 'No invoices match the selected filters.'
          : 'Could not generate the GST report. Try narrowing the date range.',
      );
    } finally {
      setExportingReport(false);
    }
  };

  const handleInvoice = async (bookingId: string) => {
    setInvoicingId(bookingId);
    try {
      const res = await api.get(API_ENDPOINTS.REPORTS.GST_INVOICE(bookingId));
      const inv = ((res.data as any)?.data ?? res.data) as GstInvoice;
      openGstInvoice(inv);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not generate the invoice.');
    } finally {
      setInvoicingId(null);
    }
  };

  const s = data?.summary;
  const cards = [
    { label: 'Total GST Collected', value: inr(s?.total_gst), icon: Receipt, color: 'text-emerald-600', sub: `${s?.invoice_count ?? 0} invoices` },
    { label: 'B2B GST (with GSTIN)', value: inr(s?.b2b_gst), icon: FileText, color: 'text-violet-600', sub: `${s?.b2b_count ?? 0} B2B invoices` },
    { label: 'B2C GST (no GSTIN)', value: inr(s?.b2c_gst), icon: FileText, color: 'text-amber-600', sub: `${s?.b2c_count ?? 0} B2C invoices` },
    { label: 'CGST', value: inr(s?.total_cgst), icon: IndianRupee, color: 'text-blue-600' },
    { label: 'SGST', value: inr(s?.total_sgst), icon: IndianRupee, color: 'text-violet-600' },
    { label: 'Taxable Value', value: inr(s?.total_taxable), icon: FileText, color: 'text-slate-600' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="GST Report" description="GST collected on paid deliveries — split by B2B (with GSTIN) and B2C" />

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <Card key={c.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium">
                <c.icon className={`h-4 w-4 ${c.color}`} />
                {c.label}
              </div>
              <div className="mt-2 text-xl font-bold">{c.value}</div>
              {c.sub && <div className="text-xs text-muted-foreground mt-0.5">{c.sub}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
            <div>
              <Label htmlFor="month-num">Month</Label>
              <div className="mt-1 flex gap-2">
                <select
                  id="month-num"
                  value={monthNum}
                  onChange={(e) => onMonthPartChange('month', e.target.value)}
                  className="flex h-10 w-36 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Month</option>
                  {monthNumOptions.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  id="month-year"
                  value={yearNum}
                  onChange={(e) => onMonthPartChange('year', e.target.value)}
                  className="flex h-10 w-24 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Year</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="from">From</Label>
              <div className="mt-1">
                <DatePicker
                  value={parseISODate(from)}
                  onChange={(d) => { setFrom(toISODate(d)); clearMonthSelectors(); }}
                  placeholder="Select date"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="to">To</Label>
              <div className="mt-1">
                <DatePicker
                  value={parseISODate(to)}
                  onChange={(d) => { setTo(toISODate(d)); clearMonthSelectors(); }}
                  placeholder="Select date"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="type">Type</Label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as GstType)}
                className="mt-1 flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">All</option>
                <option value="b2b">B2B (with GSTIN)</option>
                <option value="b2c">B2C (no GSTIN)</option>
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <Label htmlFor="gstin">GSTIN</Label>
              <Input id="gstin" className="mt-1" placeholder="Search by GSTIN" value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} />
            </div>
            <div>
              <Label>Quick range</Label>
              <div className="mt-1 flex gap-1.5">
                {QUICK_RANGES.map((r) => (
                  <Button
                    key={r.months}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => applyQuickRange(r.months)}
                  >
                    {r.label}
                  </Button>
                ))}
              </div>
            </div>
            <Button onClick={apply}>
              <Search className="h-4 w-4 mr-2" /> Apply
            </Button>
            <Button variant="outline" onClick={clearFilters}>Clear</Button>
            <Button
              variant="outline"
              onClick={handleDownloadAllPdf}
              disabled={downloadingAll || !data?.summary?.invoice_count}
            >
              <Download className="h-4 w-4 mr-2" />
              {downloadingAll ? 'Preparing PDF…' : `Download All as PDF${s?.invoice_count ? ` (${s.invoice_count})` : ''}`}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportGstReport}
              disabled={exportingReport || !data?.summary?.invoice_count}
            >
              <FileText className="h-4 w-4 mr-2" />
              {exportingReport ? 'Preparing…' : 'Download GST Report (PDF)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : !data?.transactions?.length ? (
            <div className="text-center py-12 text-muted-foreground">No GST records for the selected filters.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">CGST</TableHead>
                  <TableHead className="text-right">SGST</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total Paid</TableHead>
                  <TableHead className="text-right">Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.transactions.map((t) => (
                  <TableRow key={t.booking_id}>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDate(t.created_at)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{t.customer_name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground">{t.customer_phone || ''}</div>
                    </TableCell>
                    <TableCell>
                      {t.customer_gstin
                        ? <Badge className="bg-violet-100 text-violet-700">B2B</Badge>
                        : <Badge className="bg-slate-100 text-slate-600">B2C</Badge>}
                    </TableCell>
                    <TableCell>
                      {t.customer_gstin
                        ? <Badge variant="outline" className="font-mono">{t.customer_gstin}</Badge>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">{inr(t.delivery_charge)}</TableCell>
                    <TableCell className="text-right">{inr(t.cgst_amount)}</TableCell>
                    <TableCell className="text-right">{inr(t.sgst_amount)}</TableCell>
                    <TableCell className="text-right font-semibold">{inr(t.gst_amount)} <span className="text-xs text-muted-foreground">({t.gst_percent || 0}%)</span></TableCell>
                    <TableCell className="text-right font-semibold">{inr(t.total)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleInvoice(t.booking_id)}
                        disabled={invoicingId === t.booking_id}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {invoicingId === t.booking_id ? '…' : 'Invoice'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {data.total_pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
